import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { setParentOrgId } from "../../../store/slices/OrganizationSlice";
import { fetchOrganizationDetailsAsync } from "../../../store/thunks/OrganizationThunk";
import { fetchOrganizationAncestorsAsync } from "../../../store/thunks/OrganizationsV3Thunk";
import { fetchUsersListAsync } from "../../../store/thunks/UsersThunk";
import { canViewOrganizations } from "../../../constants/organizations";
import { Button } from "../../Catalyst/button";
import { Link } from "../../Catalyst/link";
import { PaginationNext, PaginationPrevious } from "../../Catalyst/pagination";
import BackIcon from "../../../assets/BackIcon";
import GlobalSearchField from "./GlobalSearchField";
import OrgUsersTable, { OrgUser } from "./OrgUsersTable";

const USERS_PER_PAGE = 10;

const LoadingState = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="h-8 w-8 animate-spin text-zinc-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  </div>
);

// ADM-5: dedicated "{Org Name} Users" page, reached from "View Users" in the org
// list. Scoped to the selected org only — distinct from the global user list.
const OrgUsers = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const parsedOrgId = orgId ? parseInt(orgId, 10) : NaN;
  const selectedOrgId = Number.isNaN(parsedOrgId) ? null : parsedOrgId;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);
  const orgDetails = useSelector((state: RootState) => state.OrganizationListStore?.selectedOrgDetails);
  const { ancestors, ancestorsOrgId } = useSelector(
    (state: RootState) => state.OrganizationsV3Store
  );
  const { users, status, error, total_pages } = useSelector((state: RootState) => state.UserStore);

  const [currentPage, setCurrentPage] = useState(1);
  // Set when the org details fetch rejects (unknown/deleted org id in the URL).
  const [orgNotFound, setOrgNotFound] = useState(false);

  // Details/ancestors in the store may belong to a previously viewed org.
  const currentOrg = orgDetails?.id === selectedOrgId ? orgDetails : null;
  const breadcrumbAncestors = ancestorsOrgId === selectedOrgId ? ancestors : [];

  // Keep the app-wide org context in sync (sidebar links, header) and load the
  // org's name + ancestors for the title, breadcrumb and back arrow.
  useEffect(() => {
    if (selectedOrgId == null) return;
    setOrgNotFound(false);
    dispatch(setParentOrgId(selectedOrgId));
    dispatch(fetchOrganizationDetailsAsync(selectedOrgId)).then((result) => {
      if (fetchOrganizationDetailsAsync.rejected.match(result)) setOrgNotFound(true);
    });
    dispatch(fetchOrganizationAncestorsAsync(selectedOrgId));
  }, [selectedOrgId, dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedOrgId == null) return;
    dispatch(
      fetchUsersListAsync({ page: currentPage, per_page: USERS_PER_PAGE, orgId: selectedOrgId })
    );
  }, [selectedOrgId, currentPage, dispatch]);

  // Back returns to the parent org's sub-org list — the list this org's row is on.
  const handleBack = () => {
    const parentOrgId = currentOrg?.parentOrgId;
    navigate(parentOrgId ? `/organizations/${parentOrgId}` : "/organizations");
  };

  const handleViewActivity = (user: OrgUser) => {
    if (selectedOrgId == null) return;
    // backTo lets the profile's back button return here instead of the global list.
    navigate(`/user-profile/${selectedOrgId}/${user.id}`, {
      state: { backTo: `/organizations/${selectedOrgId}/users`, backLabel: "Back" },
    });
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > total_pages) return;
    setCurrentPage(nextPage);
  };

  if (!userAccount) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (orgNotFound) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
          <div className="desktext14 text-texthead">Organization not found</div>
          <p className="max-w-md text-sm text-textColor">
            This organization does not exist or is no longer available.
          </p>
          <Button outline onClick={() => navigate("/organizations")}>
            Back to organizations
          </Button>
        </div>
      </div>
    );
  }

  if (!canViewOrganizations(userAccount.user_role) || selectedOrgId == null) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <div className="desktext14 text-texthead">Access restricted</div>
          <p className="max-w-md text-sm text-textColor">
            You do not have permission to view this page. Please contact an administrator if you
            believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  const isLoading = status === "loading" || status === "idle";
  const isFailed = status === "failed";
  const hasResults = !isLoading && !isFailed && users.length > 0;
  const orgName = currentOrg?.name ?? "";

  return (
    <div className="p-8">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Button outline onClick={handleBack} title="Back to organization list" aria-label="Back to organization list">
            <BackIcon />
          </Button>
          <h1 className="desktext10 truncate">{orgName}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <GlobalSearchField className="hidden w-[360px] md:block xl:w-[490px]" />
        </div>
      </div>

      {/* Breadcrumb: Organization / ancestors… / {org} / Users */}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm font-medium text-texthead">
        <Link href="/organizations" className="hover:underline">
          Organization
        </Link>
        {breadcrumbAncestors.map((ancestor) => (
          <Fragment key={ancestor.id}>
            <span className="opacity-50">/</span>
            <Link href={`/organizations/${ancestor.id}`} className="hover:underline">
              {ancestor.name}
            </Link>
          </Fragment>
        ))}
        <span className="opacity-50">/</span>
        <Link href={`/organizations/${selectedOrgId}`} className="hover:underline">
          {orgName}
        </Link>
        <span className="opacity-50">/</span>
        <span aria-current="page">Users</span>
      </div>

      {/* Users table card */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="px-6 pb-4">
          <div className="desktext11 text-texthead">{orgName ? `${orgName} Users` : "Users"}</div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isFailed ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
            <p className="max-w-md text-center text-sm font-medium text-red-500">
              {error || "Something went wrong while loading users. Please try again."}
            </p>
          </div>
        ) : !hasResults ? (
          <div className="flex flex-col items-center justify-center gap-2 py-20">
            <p className="text-sm font-medium text-texthead opacity-70">No users found</p>
            <p className="max-w-md text-center text-sm text-textColor">
              This organization has no users yet.
            </p>
          </div>
        ) : (
          <div className="px-6">
            <OrgUsersTable users={users} onViewActivity={handleViewActivity} />
          </div>
        )}

        {hasResults && (
          <div className="flex items-center justify-between border-t border-shadowborder px-6 py-3">
            <span className="text-sm font-medium text-textColor3">
              Page {currentPage} of {total_pages}
            </span>
            <div className="flex items-center gap-3">
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === total_pages}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgUsers;
